import React, { useState, useEffect } from 'react';
import {
    Box, Typography, List, ListItem, ListItemText,
    CircularProgress, Alert, Link, Stack, Paper, Divider,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import LinksTable, { LinkItem } from '../LinksTable';
import { fetchMotownSchedule, fetchMotownStandings } from '../../utils/tournamentScraper';
import { formatLocation, getLiveBarnLink } from '../../utils/eventUtils';
import { MhrRating, TournamentGame, TournamentPool } from '../../types';
import { MHR_RATINGS_2025_PW_MINOR } from '../../mhr-ratings';
import { logEvent } from '../../analytics';

const motownLinks: LinkItem[] = [
    { label: 'Website', url: 'https://200x85.com/event/ccm-motown/', displayText: "CCM Motown" },
    { label: 'Jets Schedule', url: 'https://my.200x85.com/tournaments/4a17c3fc-c6af-4605-9fd6-7d35d1f48193/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb', displayText: "CCM Motown Schedule" },
    { label: 'Full Schedule', url: 'https://my.200x85.com/tournaments/4a17c3fc-c6af-4605-9fd6-7d35d1f48193/schedules?division=641', displayText: 'All 2014 Teams' },
];

const TournamentGameDisplay: React.FC<{ game: TournamentGame, ratings: Record<string, MhrRating> }> = ({ game, ratings }) => {
    const opponentTeam = game.homeTeam.includes('Jets') ? game.visitorTeam : game.homeTeam;

    const renderTeamWithRating = (teamName: string) => {
        const ratingInfo = ratings[teamName];
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">{teamName}</Typography>
                {ratingInfo && (
                    <Link
                        href={ratingInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => logEvent('click_mhr_link', { team_name: teamName })}
                        sx={{ textDecoration: 'none', ml: 1 }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            ({ratingInfo.rating.toFixed(2)})
                        </Typography>
                    </Link>
                )}
            </Box>
        );
    };

    return (
        <ListItem>
            <ListItemText
                primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                            {renderTeamWithRating(game.homeTeam)}
                            {renderTeamWithRating(game.visitorTeam)}
                        </Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                            {game.homeScore} - {game.visitorScore}
                        </Typography>
                    </Stack>
                }
                secondary={
                    <>
                        <Typography component="span" variant="body2" color="text.secondary">
                            {game.dateTime}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            {formatLocation(game.location)}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                            {game.boxscoreUrl && (
                                <Link href={game.boxscoreUrl} target="_blank" rel="noopener noreferrer" variant="body2" onClick={() => logEvent('click_tournament_boxscore', { game_id: game.id, opponent: opponentTeam })}>
                                    Box Score
                                </Link>
                            )}
                            {getLiveBarnLink(game.location, game.dateTime)}
                        </Stack>
                    </>
                }
            />
        </ListItem>
    );
};

const TournamentStandingsTable: React.FC<{ pool: TournamentPool, ratings: Record<string, MhrRating> }> = ({ pool, ratings }) => {
    return (
        <Paper variant="outlined" elevation={0} sx={{ mb: 4, borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.100' }}>
                <Typography variant="h6">{pool.poolName}</Typography>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Team</TableCell>
                            <TableCell align="right">GP</TableCell>
                            <TableCell align="right">W</TableCell>
                            <TableCell align="right">T</TableCell>
                            <TableCell align="right">L</TableCell>
                            <TableCell align="right">PTS</TableCell>
                            <TableCell align="right">GF</TableCell>
                            <TableCell align="right">GA</TableCell>
                            <TableCell align="right">GD</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pool.standings.map((team) => (
                            <TableRow key={team.teamName}>
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {team.teamUrl ? (
                                            <Link href={team.teamUrl} target="_blank" rel="noopener noreferrer" onClick={() => logEvent('click_tournament_team_link', { team_name: team.teamName })} color="inherit" underline="hover">
                                                <Typography variant="body2">{team.teamName}</Typography>
                                            </Link>
                                        ) : (
                                            <Typography variant="body2">{team.teamName}</Typography>
                                        )}
                                        {ratings[team.teamName] && (
                                            <Link
                                                href={ratings[team.teamName].url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => logEvent('click_mhr_link', { team_name: team.teamName })}
                                                sx={{ textDecoration: 'none', ml: 1 }}
                                            >
                                                <Typography variant="caption" color="text.secondary">
                                                    ({ratings[team.teamName].rating.toFixed(2)})
                                                </Typography>
                                            </Link>
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">{team.gp}</TableCell>
                                <TableCell align="right">{team.w}</TableCell>
                                <TableCell align="right">{team.t}</TableCell>
                                <TableCell align="right">{team.l}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{team.pts}</TableCell>
                                <TableCell align="right">{team.gf}</TableCell>
                                <TableCell align="right">{team.ga}</TableCell>
                                <TableCell align="right">{team.gd}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

const CcmMotownPage = () => {
    const [games, setGames] = useState<TournamentGame[]>([]);
    const [standings, setStandings] = useState<TournamentPool[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [scheduleData, standingsData] = await Promise.all([
                    fetchMotownSchedule(),
                    fetchMotownStandings()
                ]);
                setGames(scheduleData);
                setStandings(standingsData);
            } catch (e: any) {
                setError(e.message);
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    return (
        <>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
            {!loading && !error && (
                <>
                    {standings.map(pool => <TournamentStandingsTable key={pool.poolName} pool={pool} ratings={MHR_RATINGS_2025_PW_MINOR} />)}
                    <Paper variant="outlined" elevation={0} sx={{ mb: 4, borderRadius: 2 }}>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.100' }}>
                            <Typography variant="h6">Schedule & Results</Typography>
                        </Box>
                        {games.length > 0 ? (
                            <List disablePadding>{games.map((game, index) => <React.Fragment key={game.id}>{index > 0 && <Divider component="li" /> }<TournamentGameDisplay game={game} ratings={MHR_RATINGS_2025_PW_MINOR} /></React.Fragment>)}</List>
                        ) : (
                            <Typography sx={{ p: 2 }}>Schedule not yet available.</Typography>
                        )}
                    </Paper>
                </>
            )}
            <LinksTable title="Tournament Links" links={motownLinks} />
        </>
    );
};

export default CcmMotownPage;